import React, { useState } from 'react';
import Plot from 'react-plotly.js';
import {
    Typography,
    Box,
    CircularProgress,
    Tabs,
    Tab,
    TextField,
    Button,
    Grid,
    Alert
} from '@mui/material';
import { callApi } from '../utils/apiHelper';
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import GridLayoutWrapper from './GridLayoutWrapper';
import GridItem from './GridItem';
import useGridLayoutPersistence from '../hooks/useGridLayoutPersistence';
import { DEFAULT_ALL_LAYOUTS } from '../utils/layoutDefaults';
import { CHART_IDS, CHART_KEY_MAP } from '../utils/layoutConstants';

function DashboardPage({ mode }) {
    // State management
    const [chartData, setChartData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        partition_value: 'daily',
        start_date: '2024-11-01',
        end_date: '2024-12-31'
    });
    const [activeTab, setActiveTab] = useState(0);
    const [filterValidation, setFilterValidation] = useState({
        partition_value: true,
        start_date: true,
        end_date: true,
        date_range: true
    });

    // Grid layout persistence
    const { layouts, setLayouts } = useGridLayoutPersistence(DEFAULT_ALL_LAYOUTS);

    // Card styling with enhanced dark/light mode support
    const cardStyle = {
        padding: '1em',
        border: `1px solid ${mode === 'light' ? '#e0e0e0' : '#555'}`,
        borderRadius: '15px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        backgroundColor: mode === 'light' ? '#f9f9f9' : '#2c2c2c',
        color: mode === 'light' ? '#000' : '#fff',
        transition: 'background-color 0.3s, color 0.3s'
    };

    // Validate filters
    const validateFilters = () => {
        const validations = {
            partition_value: filters.partition_value.trim() !== '',
            start_date: filters.start_date !== '',
            end_date: filters.end_date !== ''
        };

        // Additional validation for date range
        if (validations.start_date && validations.end_date) {
            validations.date_range = new Date(filters.start_date) <= new Date(filters.end_date);
        }

        setFilterValidation(validations);

        // Return true if all validations pass
        return Object.values(validations).every(v => v === true);
    };

    // Fetch charts from API
    const fetchCharts = async () => {
        // Validate filters before fetching
        if (!validateFilters()) {
            setError('Please check your filter inputs');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = {
                filters: filters,
                charts: [
                    "conv_attr/organic_vs_paid",
                    "conv_attr/waterfall_by_variable",
                    "conv_attr/organic_vs_paid_over_time",
                    "conv_attr/paid_decomposition_over_time",
                    "bayes_eng_attr/avg_cac",
                    "bayes_eng_attr/marginal_cac",
                    "bayes_eng_attr/spend_vs_attr_share",
                    "response_curve/rc_vs_actual"
                ]
            };

            const response = await callApi({
                path: '/ts-service-main/plot',
                method: 'POST',
                body: payload,
                contentType: 'application/json'
            });

            // Parse JSON strings to objects
            const parsedCharts = {};
            Object.keys(response).forEach(key => {
                parsedCharts[key] = JSON.parse(response[key]);
            });

            setChartData(parsedCharts);
        } catch (err) {
            console.error('Error fetching charts:', err);
            setError(err.message || 'Failed to load charts');
        } finally {
            setLoading(false);
        }
    };

    // Handle filter changes with validation
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));

        // Reset specific validation when typing
        setFilterValidation(prev => ({
            ...prev,
            [name]: true
        }));
    };

    // Trigger chart reload
    const handleLoadCharts = () => {
        fetchCharts();
    };

    // Handle layout changes
    const handleLayoutChange = (layout, allLayouts, tabId) => {
        setLayouts(allLayouts);
    };

    // Handle layout reset
    const handleResetLayout = () => {
        setLayouts(DEFAULT_ALL_LAYOUTS);
        try {
            localStorage.removeItem('dashboard_layouts_v2');
        } catch (error) {
            console.error('Failed to reset layout:', error);
        }
    };

    // Charts are loaded manually via the "Load Charts" button

    // Render individual chart with dark/light mode support
    const renderChart = (chartKey, title) => {
        const chartConfig = chartData[chartKey];

        if (!chartConfig) {
            return <Typography color="text.secondary">Chart not available</Typography>;
        }

        return (
            <Box sx={{
                border: `1px solid ${mode === 'light' ? '#e0e0e0' : '#555'}`,
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: mode === 'light' ? '#fff' : '#333',
                transition: 'background-color 0.3s, border-color 0.3s',
                width: '100%',
                height: '100%'
            }}>
                <Plot
                    data={chartConfig.data}
                    layout={{
                        ...chartConfig.layout,
                        autosize: true,
                        paper_bgcolor: 'transparent',
                        plot_bgcolor: 'transparent',
                        font: {
                            color: mode === 'light' ? '#333' : '#ddd',
                            family: 'Arial, sans-serif'
                        },
                        margin: { l: 50, r: 50, t: 50, b: 50 }
                    }}
                    config={{
                        responsive: true,
                        displayModeBar: false,
                        staticPlot: false
                    }}
                    style={{ width: '100%', height: '100%' }}
                    useResizeHandler
                />
            </Box>
        );
    };

    return (
        <Box sx={{
            width: '100%',
            marginTop: '1em',
            backgroundColor: mode === 'light' ? '#fff' : '#1e1e1e',
            color: mode === 'light' ? '#000' : '#fff',
            transition: 'background-color 0.3s, color 0.3s'
        }}>
            {/* Filter Controls */}
            <Box sx={{ ...cardStyle, marginBottom: '1em' }}>
                <Typography variant="h6" sx={{ marginBottom: '1em' }}>
                    Chart Filters
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Partition Value"
                            name="partition_value"
                            value={filters.partition_value}
                            onChange={handleFilterChange}
                            variant="outlined"
                            size="small"
                            error={!filterValidation.partition_value}
                            helperText={!filterValidation.partition_value ? "Partition value is required" : ""}
                            InputProps={{
                                sx: {
                                    backgroundColor: mode === 'light' ? '#fff' : '#333',
                                    color: mode === 'light' ? '#000' : '#fff'
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Start Date"
                            name="start_date"
                            type="date"
                            value={filters.start_date}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                            error={!filterValidation.start_date}
                            helperText={!filterValidation.start_date ? "Start date is required" : ""}
                            InputProps={{
                                sx: {
                                    backgroundColor: mode === 'light' ? '#fff' : '#333',
                                    color: mode === 'light' ? '#000' : '#fff'
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="End Date"
                            name="end_date"
                            type="date"
                            value={filters.end_date}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                            error={!filterValidation.end_date || !filterValidation.date_range}
                            helperText={
                                !filterValidation.end_date
                                    ? "End date is required"
                                    : (!filterValidation.date_range
                                        ? "End date must be after start date"
                                        : "")
                            }
                            InputProps={{
                                sx: {
                                    backgroundColor: mode === 'light' ? '#fff' : '#333',
                                    color: mode === 'light' ? '#000' : '#fff'
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleLoadCharts}
                            disabled={loading}
                            sx={{
                                backgroundColor: mode === 'light'
                                    ? 'primary.main'
                                    : 'primary.dark',
                                '&:hover': {
                                    backgroundColor: mode === 'light'
                                        ? 'primary.dark'
                                        : 'primary.light'
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Load Charts'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* Reset Layout Button */}
            <Box sx={{ ...cardStyle, marginBottom: '1em' }}>
                <Button
                    variant="outlined"
                    onClick={handleResetLayout}
                    sx={{
                        borderColor: mode === 'light' ? 'primary.main' : 'primary.light',
                        color: mode === 'light' ? 'primary.main' : 'primary.light',
                        '&:hover': {
                            backgroundColor: mode === 'light' ? 'rgba(25, 118, 210, 0.04)' : 'rgba(144, 202, 249, 0.04)'
                        }
                    }}
                >
                    Reset Layout
                </Button>
            </Box>

            {/* Error Handling */}
            {error && (
                <Box sx={{ ...cardStyle, marginBottom: '1em' }}>
                    <Alert severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                </Box>
            )}

            {/* Dashboard content */}
            <Box sx={{ width: '100%' }}>
                {/* Header */}
                <Box sx={{ ...cardStyle, marginBottom: '1em' }}>
                    <Typography variant="h2" sx={{
                        fontSize: '1.5rem',
                        color: mode === 'light' ? '#000' : '#fff'
                    }}>
                        Attribution Dashboard
                    </Typography>
                </Box>

                {/* Tabs */}
                {!error && (
                    <Box sx={{ ...cardStyle, marginBottom: '1em' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                                '& .MuiTab-root': {
                                    color: mode === 'light' ? '#000' : '#fff'
                                },
                                '& .Mui-selected': {
                                    color: mode === 'light' ? 'primary.main' : 'primary.light'
                                }
                            }}
                        >
                            <Tab label="Outcome Composition" />
                            <Tab label="Performance" />
                            <Tab label="Planning" />
                        </Tabs>

                        {/* Tab 0: Outcome Composition - 2x2 Grid with react-grid-layout */}
                        {activeTab === 0 && (
                            <GridLayoutWrapper
                                layouts={layouts}
                                onLayoutChange={handleLayoutChange}
                                mode={mode}
                                tabId="outcome"
                            >
                                <GridItem
                                    key={CHART_IDS.ORGANIC_VS_PAID}
                                    id={CHART_IDS.ORGANIC_VS_PAID}
                                    title="Organic vs. Paid"
                                    mode={mode}
                                >
                                    {renderChart(CHART_KEY_MAP[CHART_IDS.ORGANIC_VS_PAID], 'Organic vs. Paid')}
                                </GridItem>

                                <GridItem
                                    key={CHART_IDS.WATERFALL}
                                    id={CHART_IDS.WATERFALL}
                                    title="Decomposition by Variable"
                                    mode={mode}
                                >
                                    {renderChart(CHART_KEY_MAP[CHART_IDS.WATERFALL], 'Decomposition by Variable')}
                                </GridItem>

                                <GridItem
                                    key={CHART_IDS.ORGANIC_VS_PAID_TIME}
                                    id={CHART_IDS.ORGANIC_VS_PAID_TIME}
                                    title="Paid vs. Organic Over Time"
                                    mode={mode}
                                >
                                    {renderChart(CHART_KEY_MAP[CHART_IDS.ORGANIC_VS_PAID_TIME], 'Paid vs. Organic Over Time')}
                                </GridItem>

                                <GridItem
                                    key={CHART_IDS.PAID_DECOMP_TIME}
                                    id={CHART_IDS.PAID_DECOMP_TIME}
                                    title="Paid Decomposition Over Time"
                                    mode={mode}
                                >
                                    {renderChart(CHART_KEY_MAP[CHART_IDS.PAID_DECOMP_TIME], 'Paid Decomposition Over Time')}
                                </GridItem>
                            </GridLayoutWrapper>
                        )}

                        {/* Tab 1: Performance - CAC Charts with react-grid-layout */}
                        {activeTab === 1 && (
                            <GridLayoutWrapper
                                layouts={layouts}
                                onLayoutChange={handleLayoutChange}
                                mode={mode}
                                tabId="performance"
                            >
                                <GridItem
                                    key={CHART_IDS.AVG_CAC}
                                    id={CHART_IDS.AVG_CAC}
                                    title="Average CAC"
                                    mode={mode}
                                >
                                    {renderChart(CHART_KEY_MAP[CHART_IDS.AVG_CAC], 'Average CAC')}
                                </GridItem>

                                <GridItem
                                    key={CHART_IDS.MARGINAL_CAC}
                                    id={CHART_IDS.MARGINAL_CAC}
                                    title="Marginal CAC"
                                    mode={mode}
                                >
                                    {renderChart(CHART_KEY_MAP[CHART_IDS.MARGINAL_CAC], 'Marginal CAC')}
                                </GridItem>

                                <GridItem
                                    key={CHART_IDS.SPEND_VS_ATTR_SHARE}
                                    id={CHART_IDS.SPEND_VS_ATTR_SHARE}
                                    title="Spend vs. Attribution Share"
                                    mode={mode}
                                >
                                    {renderChart(CHART_KEY_MAP[CHART_IDS.SPEND_VS_ATTR_SHARE], 'Spend vs. Attribution Share')}
                                </GridItem>
                            </GridLayoutWrapper>
                        )}

                        {/* Tab 2: Response Curve */}
                        {activeTab === 2 && (
                            <GridLayoutWrapper
                                layouts={layouts}
                                onLayoutChange={handleLayoutChange}
                                mode={mode}
                                tabId="response_curve"
                            >
                                <GridItem
                                    key={CHART_IDS.RC_VS_ACTUAL}
                                    id={CHART_IDS.RC_VS_ACTUAL}
                                    title="Response Curve vs Actual"
                                    mode={mode}
                                >
                                    {renderChart(CHART_KEY_MAP[CHART_IDS.RC_VS_ACTUAL], 'Response Curve vs Actual')}
                                </GridItem>
                            </GridLayoutWrapper>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default DashboardPage;
